// src/pages/base/components/BaseHeader.jsx

import React, { useState, useEffect } from 'react';
import { Clock, FastForward } from 'lucide-react';
import { getColorClasses } from '../../themeConfig';
import { getTabDefinitions, getResourceItems } from '../Base.constants';

const BaseHeader = ({
  config,
  currentCampaign,
  resources,
  handleAdvanceDay,
  isAdvancingDay,
  dayProgress,
  selectedTab,
  setSelectedTab
}) => {
  const tabs = getTabDefinitions(config);
  const resourceItems = getResourceItems(config);
  
  const [displayTime, setDisplayTime] = useState('00:00');
  const [displayDate, setDisplayDate] = useState('');
  const [lastAdvancedDay, setLastAdvancedDay] = useState(currentCampaign?.day);

  // Default fallback date
  const DEFAULT_START_DATE = '2025-01-01';
  const startDate = config.startDate || DEFAULT_START_DATE;

  // Calculate displayed date and time
  useEffect(() => {
    const calculateDateTime = () => {
      // Parse start date
      const start = new Date(startDate);
      
      // Calculate target date by adding days
      const currentDay = currentCampaign?.day || 1;
      const targetDate = new Date(start);
      targetDate.setDate(targetDate.getDate() + (currentDay - 1));
      
      // Format date as "MMM DD, YYYY"
      const dateStr = targetDate.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
      }).toUpperCase();
      
      setDisplayDate(dateStr);
    };

    calculateDateTime();
  }, [currentCampaign?.day, startDate]);

  // Calculate time based on dayProgress (0-100%)
  useEffect(() => {
    // When day advances, reset to 00:00
    if (currentCampaign?.day !== lastAdvancedDay) {
      setDisplayTime('00:00');
      setLastAdvancedDay(currentCampaign?.day);
    } else {
      // Calculate time: 0% = 00:00, 100% = 23:59
      const totalMinutesInDay = 24 * 60 - 1; // 1439 minutes (00:00 to 23:59)
      const currentMinutes = Math.floor((dayProgress / 100) * totalMinutesInDay);
      const hours = Math.floor(currentMinutes / 60);
      const minutes = currentMinutes % 60;
      
      const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      setDisplayTime(timeStr);
    }
  }, [dayProgress, currentCampaign?.day, lastAdvancedDay]);

  // Color configuration object for cleaner code
  const colorConfig = {
    amber: { 
      bg: 'linear-gradient(180deg, rgba(180, 83, 9, 0.05) 0%, rgba(120, 53, 15, 0.4) 100%)', 
      border: 'rgba(180, 83, 9, 0.5)', 
      glow: 'rgba(180, 83, 9, 0.1)' 
    },
    orange: { 
      bg: 'linear-gradient(180deg, rgba(194, 65, 12, 0.05) 0%, rgba(124, 45, 18, 0.4) 100%)', 
      border: 'rgba(194, 65, 12, 0.5)', 
      glow: 'rgba(194, 65, 12, 0.1)' 
    },
    cyan: { 
      bg: 'linear-gradient(180deg, rgba(6, 182, 212, 0.05) 0%, rgba(15, 118, 110, 0.4) 100%)', 
      border: 'rgba(6, 182, 212, 0.5)', 
      glow: 'rgba(6, 182, 212, 0.1)' 
    },
    blue: { 
      bg: 'linear-gradient(180deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.4) 100%)', 
      border: 'rgba(59, 130, 246, 0.5)', 
      glow: 'rgba(59, 130, 246, 0.1)' 
    },
    purple: { 
      bg: 'linear-gradient(180deg, rgba(168, 85, 247, 0.05) 0%, rgba(126, 34, 206, 0.4) 100%)', 
      border: 'rgba(168, 85, 247, 0.5)', 
      glow: 'rgba(168, 85, 247, 0.1)' 
    },
    emerald: { 
      bg: 'linear-gradient(180deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.4) 100%)', 
      border: 'rgba(16, 185, 129, 0.5)', 
      glow: 'rgba(16, 185, 129, 0.1)' 
    }
  };
  
  const colors = colorConfig[config.colors.primary] || colorConfig.blue;

  return (
    <div className="relative backdrop-blur border-b-2 sticky top-0 z-50" style={{
      background: colors.bg,
      borderColor: colors.border,
      boxShadow: `inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 8px 24px ${colors.glow}, 0 2px 4px rgba(0, 0, 0, 0.5)`
    }}>
      {/* Subtle top highlight for depth */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      
      {/* Noise texture overlay for industrial feel */}
      <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noiseFilter"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" seed="2"/%3E%3C/filter%3E%3Crect width="400" height="400" fill="%23000" filter="url(%23noiseFilter)"/%3E%3C/svg%3E")'
      }} />
      
      <div className="max-w-7xl mx-auto px-4 py-4 relative z-10">
        <div className="flex items-center justify-between">
          {/* Title */}
          <div>
            <h1 className={`text-3xl font-bold ${getColorClasses(config.colors.primary)} tracking-wider`}>
              {config.baseTitle || 'BASE OPERATIONS'}
            </h1>
            <p className="text-sm text-gray-400 font-mono">
              CAMPAIGN: {currentCampaign?.name?.toUpperCase() || 'UNKNOWN'} | D+{currentCampaign?.day || 1} | {displayDate} - {displayTime}
            </p>
          </div>
          
          {/* Resources and Advance Day Button */}
          <div className="flex gap-6 items-center">
            {/* Resources Display */}
            <div className="flex gap-6 items-center">
              {resourceItems.map(({ icon: Icon, label, getValue, color }) => (
                <div key={label} className="flex items-center gap-2">
                  <Icon className={color} size={20} />
                  <div>
                    <p className="text-xs text-gray-400">{label}</p>
                    <p className={`font-bold ${color}`}>{getValue(resources)}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Advance Day Button */}
            <button
              onClick={handleAdvanceDay}
              disabled={isAdvancingDay}
              className={`relative overflow-hidden flex items-center gap-2 px-6 py-3 font-bold text-sm tracking-wider transition-all rounded shadow-lg ${
                isAdvancingDay
                  ? 'bg-gray-600 cursor-not-allowed'
                  : `${getColorClasses(config.colors.primary, 'bgMain')} ${getColorClasses(config.colors.primary, 'bgHover')}`
              }`}
            >
              {/* Progress bar fill */}
              <div 
                className={`absolute inset-0 ${getColorClasses(config.colors.secondary, 'bgMain')} opacity-30 transition-all duration-1000`}
                style={{ width: `${dayProgress}%` }}
              />
              
              {/* Button content */}
              <div className="relative z-10 flex items-center gap-2">
                {isAdvancingDay ? (
                  <>
                    <Clock className="animate-spin" size={20} />
                    <span>PROCESSING...</span>
                  </>
                ) : (
                  <>
                    <FastForward size={20} />
                    <span>ADVANCE DAY</span>
                  </>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mt-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`px-6 py-2 font-bold text-sm tracking-wider transition-all rounded-t ${
                selectedTab === tab.id
                  ? `${getColorClasses(config.colors.primary, 'bgMain')} text-white shadow-lg`
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BaseHeader;